import { memo, useMemo } from "react";
import styled from "styled-components";
import moment from "moment";
import NewDomain from "types/newDomain";
import { Spinner } from "react-bootstrap";
import SmartLink from "components/smartLink/SmartLink";
import { useTranslation } from "react-i18next";

const Container = styled.div`
  width: 100%;
  max-width: 1000px;
  margin-top: 70px;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: #16a34a;
  margin-bottom: 10px;
  text-align: center;

  @media only screen and (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: #666666;
  text-align: center;
  margin-bottom: 25px;
  line-height: 1.5;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media only screen and (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0 10px;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 420px));
  gap: 20px;
  width: 100%;
  justify-content: center;

  @media only screen and (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(SmartLink)`
  background: white;
  border: solid 1px #e8e8ef;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
  text-decoration: none;
  cursor: pointer;
  min-width: 340px;
  max-width: 420px;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #28c604;
    text-decoration: none;
  }

  @media only screen and (max-width: 576px) {
    min-width: unset;
    max-width: unset;
  }
`;

const DomainName = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: #111;
  margin-bottom: 15px;
  word-break: break-word;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  @media only screen and (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const NewBadge = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 500;
  color: #b45309;
  background: #fff3cd;
  padding: 4px 8px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const InfoRow = styled.div`
  font-size: 0.95rem;
  color: #555555;
  margin-bottom: 8px;
  line-height: 1.4;
`;

const BoldNumber = styled.strong`
  font-weight: 600;
  color: #333333;
`;

const ViewCTA = styled.div`
  font-size: 0.9rem;
  color: #28c604;
  font-weight: 500;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #28c604;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666666;
  font-size: 1rem;
`;

interface NewDomainsGridProps {
  domains: NewDomain[];
  loading: boolean;
}

const NewDomainsGrid = (props: NewDomainsGridProps) => {
  const { domains, loading } = props;
  const { t } = useTranslation("home");

  // Sort domains by expiration date (most recent first)
  const sortedDomains = useMemo(() => {
    if (!domains) return [];

    const domainsWithAge = domains.map((domain) => {
      const lastRegistered = moment(domain.lastRegisteredOnUtc);
      const ageInDays = moment().diff(lastRegistered, "days");
      const expiredDate = moment(domain.expiredOnUtc);
      const daysLapsed = moment().diff(expiredDate, "days");
      const isNewlyAvailable = daysLapsed <= 7;
      return { ...domain, ageInDays, isNewlyAvailable, expiredDate };
    });

    return domainsWithAge.sort(
      (a, b) => b.expiredDate.valueOf() - a.expiredDate.valueOf(),
    );
  }, [domains]);

  const formatAge = (
    days: number,
  ): { value: string | number; unit: string } => {
    if (days === 0) return { value: t("newDomains.today"), unit: "" };
    if (days === 1) return { value: 1, unit: t("newDomains.dayOld") };
    if (days < 30) return { value: days, unit: t("newDomains.dayOld") };

    const months = Math.floor(days / 30);
    if (months === 1) return { value: 1, unit: t("newDomains.monthOld") };
    if (months < 12) return { value: months, unit: t("newDomains.monthOld") };

    const years = Math.floor(days / 365);
    if (years === 1) return { value: 1, unit: t("newDomains.yearOld") };
    return { value: years, unit: t("newDomains.yearOld") };
  };

  if (loading) {
    return (
      <Container>
        <Title>{t("newDomains.title")}</Title>
        <SpinnerWrapper>
          <Spinner animation="border" role="status" aria-hidden="true" />
        </SpinnerWrapper>
      </Container>
    );
  }

  if (!domains || domains.length === 0) {
    return (
      <Container>
        <Title>{t("newDomains.title")}</Title>
        <EmptyMessage>{t("newDomains.noResults")}</EmptyMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{t("newDomains.title")}</Title>
      <Description>{t("newDomains.description")}</Description>
      <CardsGrid>
        {sortedDomains.map((domain) => {
          const age = formatAge(domain.ageInDays);
          return (
            <Card key={domain.name} to={`/domain/${domain.name}`}>
              <DomainName>
                {domain.name}
                {domain.isNewlyAvailable && (
                  <NewBadge>{t("newDomains.newlyAvailable")}</NewBadge>
                )}
              </DomainName>
              <InfoRow>
                {age.unit === "" ? (
                  age.value
                ) : (
                  <>
                    <BoldNumber>{age.value}</BoldNumber> {age.unit}
                  </>
                )}
              </InfoRow>
              <ViewCTA>{t("newDomains.viewCta")}</ViewCTA>
            </Card>
          );
        })}
      </CardsGrid>
    </Container>
  );
};

export default memo(NewDomainsGrid);
